import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "./firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { theme } from "../styles/theme";

const ResultsPage = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const navigate = useNavigate();

  // Verifica se o usuário está autenticado
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        setError("Você precisa estar logado para acessar os resultados.");
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Busca os resultados do usuário no Firestore
  useEffect(() => {
    const fetchResults = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const resultsRef = collection(db, "testResults");
        const q = query(resultsRef, where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);

        const fetchedResults = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (fetchedResults.length > 0) {
          setResults(fetchedResults);
        } else {
          setError("Nenhum resultado encontrado.");
        }
      } catch (err) {
        setError("Erro ao carregar os resultados. Por favor, tente novamente mais tarde.");
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchResults();
    }
  }, [user]);

  // Exibição de carregamento
  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.colors.gray[50],
        fontFamily: theme.typography.fontFamily.primary,
      }}>
        <div style={{
          textAlign: "center",
          padding: theme.spacing.xl,
        }}>
          <div style={{
            width: "40px",
            height: "40px",
            border: `3px solid ${theme.colors.gray[200]}`,
            borderTop: `3px solid ${theme.colors.primary}`,
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto",
            marginBottom: theme.spacing.lg,
          }} />
          <p style={{
            fontSize: theme.typography.fontSize.lg,
            color: theme.colors.gray[600],
          }}>
            Carregando resultados...
          </p>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Exibição de erros
  if (error) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.colors.gray[50],
        fontFamily: theme.typography.fontFamily.primary,
      }}>
        <div style={{
          textAlign: "center",
          padding: theme.spacing.xl,
          maxWidth: "400px",
        }}>
          <div style={{
            fontSize: "3rem",
            marginBottom: theme.spacing.lg,
          }}>
            ⚠️
          </div>
          <h2 style={{
            fontSize: theme.typography.fontSize.xl,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.error,
            marginBottom: theme.spacing.md,
          }}>
            Ops! Algo deu errado
          </h2>
          <p style={{
            fontSize: theme.typography.fontSize.base,
            color: theme.colors.gray[600],
            marginBottom: theme.spacing.xl,
            lineHeight: 1.5,
          }}>
            {error}
          </p>
          <div style={{
            display: "flex",
            gap: theme.spacing.md,
            justifyContent: "center",
            flexWrap: "wrap",
          }}>
            <button
              onClick={() => navigate("/")}
              style={{
                padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                backgroundColor: theme.colors.primary,
                color: theme.colors.white,
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.semibold,
                cursor: "pointer",
                transition: theme.transitions.normal,
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme.colors.primaryDark;
                e.target.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = theme.colors.primary;
                e.target.style.transform = "translateY(0)";
              }}
            >
              Voltar para o Início
            </button>
            <button
          onClick={() => window.location.reload()}
              style={{
                padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                backgroundColor: "transparent",
                color: theme.colors.primary,
                border: `2px solid ${theme.colors.primary}`,
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.semibold,
                cursor: "pointer",
                transition: theme.transitions.normal,
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme.colors.primary;
                e.target.style.color = theme.colors.white;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = theme.colors.primary;
              }}
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Exibição dos resultados
  return (
    <div style={{
      minHeight: "100vh",
      backgroundColor: theme.colors.gray[50],
      padding: `${theme.spacing['2xl']} ${theme.spacing.xl}`,
      fontFamily: theme.typography.fontFamily.primary,
    }}>
      <div style={{ maxWidth: "800px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: theme.spacing['3xl'] }}>
          <h1 style={{
            fontSize: theme.typography.fontSize['3xl'],
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.gray[800],
            marginBottom: theme.spacing.md,
          }}>
            Seus Resultados
          </h1>
          <p style={{
            fontSize: theme.typography.fontSize.lg,
            color: theme.colors.gray[600],
          }}>
            Histórico completo dos seus testes
          </p>
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: theme.spacing.sm,
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadius.md,
            boxShadow: theme.shadows.sm,
            marginTop: theme.spacing.lg,
          }}>
            <div style={{
              width: "8px",
              height: "8px",
              backgroundColor: theme.colors.success,
              borderRadius: "50%",
            }} />
            <span style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.gray[600],
            }}>
              {user?.email}
            </span>
          </div>
        </div>

        {/* Estatísticas Gerais */}
        {results.length > 0 && (
          <div style={{
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadius.xl,
            padding: theme.spacing.xl,
            boxShadow: theme.shadows.md,
            marginBottom: theme.spacing.xl,
          }}>
            <h3 style={{
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.colors.gray[700],
              marginBottom: theme.spacing.lg,
              textAlign: "center",
            }}>
              📊 Resumo Geral
            </h3>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: theme.spacing.lg,
            }}>
              <div style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.primary,
                  marginBottom: theme.spacing.xs,
                }}>
                  {results.length}
                </div>
                <div style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.gray[600],
                }}>
                  Testes Realizados
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.secondary,
                  marginBottom: theme.spacing.xs,
                }}>
                  {Math.round(results.reduce((avg, result) => avg + result.score, 0) / results.length)}%
                </div>
                <div style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.gray[600],
                }}>
                  Média Geral
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.success,
                  marginBottom: theme.spacing.xs,
                }}>
                  {results.filter(r => r.score >= 72).length}
                </div>
                <div style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.gray[600],
                }}>
                  Aprovações
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Resultados */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing.lg,
        }}>
      {results.map((result) => {
            const passed = result.score >= 72;
        return (
          <div
            key={result.id}
            style={{
                  backgroundColor: theme.colors.white,
                  borderRadius: theme.borderRadius.xl,
                  padding: theme.spacing.xl,
                  boxShadow: theme.shadows.md,
                  border: `2px solid ${passed ? theme.colors.success : theme.colors.error}`,
                  transition: theme.transitions.normal,
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = theme.shadows.lg;
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = theme.shadows.md;
                }}
              >
                {/* Status Badge */}
                <div style={{
                  position: "absolute",
                  top: theme.spacing.md,
                  right: theme.spacing.md,
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  backgroundColor: passed ? theme.colors.success : theme.colors.error,
                  color: theme.colors.white,
                  borderRadius: theme.borderRadius.sm,
                  fontSize: theme.typography.fontSize.xs,
                  fontWeight: theme.typography.fontWeight.semibold,
                }}>
                  {passed ? "✅ Aprovado" : "❌ Reprovado"}
                </div>

                {/* Informações do Teste */}
                <div style={{ marginBottom: theme.spacing.lg }}>
                  <h3 style={{
                    fontSize: theme.typography.fontSize.xl,
                    fontWeight: theme.typography.fontWeight.bold,
                    color: theme.colors.gray[800],
                    marginBottom: theme.spacing.sm,
                  }}>
                    Teste {result.testId || "Sem ID"}
                  </h3>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: theme.spacing.md,
                  }}>
                    <div>
                      <span style={{
                        fontSize: theme.typography.fontSize.sm,
                        color: theme.colors.gray[600],
                      }}>
                        Pontuação:
                      </span>
                      <div style={{
                        fontSize: theme.typography.fontSize.lg,
                        fontWeight: theme.typography.fontWeight.bold,
                        color: passed ? theme.colors.success : theme.colors.error,
                      }}>
                        {result.score}%
                      </div>
                    </div>
                    <div>
                      <span style={{
                        fontSize: theme.typography.fontSize.sm,
                        color: theme.colors.gray[600],
                      }}>
                        Acertos:
                      </span>
                      <div style={{
                        fontSize: theme.typography.fontSize.lg,
                        fontWeight: theme.typography.fontWeight.bold,
                        color: theme.colors.gray[800],
                      }}>
                        {result.correctAnswers} de {result.totalQuestions}
                      </div>
                    </div>
                    <div>
                      <span style={{
                        fontSize: theme.typography.fontSize.sm,
                        color: theme.colors.gray[600],
                      }}>
                        Data:
                      </span>
                      <div style={{
                        fontSize: theme.typography.fontSize.base,
                        color: theme.colors.gray[700],
                      }}>
                        {new Date(result.date).toLocaleDateString("pt-BR")}
                      </div>
                    </div>
                  </div>
            </div>
          </div>
        );
      })}
        </div>

        {/* Botão de Voltar */}
        <div style={{ textAlign: "center", marginTop: theme.spacing['2xl'] }}>
          <button
            onClick={() => navigate("/")}
            style={{
              padding: `${theme.spacing.md} ${theme.spacing.xl}`,
              backgroundColor: theme.colors.primary,
              color: theme.colors.white,
              border: "none",
              borderRadius: theme.borderRadius.md,
              fontSize: theme.typography.fontSize.base,
              fontWeight: theme.typography.fontWeight.semibold,
              cursor: "pointer",
              transition: theme.transitions.normal,
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryDark;
              e.target.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary;
              e.target.style.transform = "translateY(0)";
            }}
          >
            Voltar para o Início
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
